// src/components/client/StylistReviews.tsx
import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import reviewService from '../../services/reviewService';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import ReviewForm from './ReviewForm';
import TeslaButton from '../common/TeslaButton';
import ProfileAvatar from '../common/ProfileAvatar';

interface StylistReviewsProps {
  stylistId: string;
}

const StylistReviews: React.FC<StylistReviewsProps> = ({ stylistId }) => {
  const [reviews, setReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editReview, setEditReview] = useState<any | null>(null);
  // The appointment ID needed to write a review for this stylist
  const [reviewableAppointmentId, setReviewableAppointmentId] = useState<string | null>(null);
  const [viewerHasReviewed, setViewerHasReviewed] = useState<boolean>(false);

  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const userReview = reviews.find(review => review.viewerIsAuthor);
  const userHasReviewed = viewerHasReviewed || Boolean(userReview);

  useEffect(() => {
    fetchReviews();
    if (isAuthenticated) fetchReviewableAppointment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stylistId, isAuthenticated]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const result = await reviewService.getStylistReviews(stylistId);

      let reviewsArray: any[];
      if (Array.isArray(result)) {
        reviewsArray = result;
      } else if (result && Array.isArray((result as any).reviews)) {
        reviewsArray = (result as any).reviews;
      } else if (result && Array.isArray((result as any).data)) {
        reviewsArray = (result as any).data;
      } else {
        reviewsArray = [];
      }

      setReviews(reviewsArray);
      setViewerHasReviewed(Boolean((result as { viewerHasReviewed?: boolean })?.viewerHasReviewed));
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  // Fetch the most recent unreviewed completed appointment with this stylist.
  // The backend requires an appointmentId to submit a review.
  const fetchReviewableAppointment = async () => {
    try {
      const appointments = await reviewService.getReviewableAppointments();
      const match = appointments.find((apt: any) => {
        const id = apt.stylist?._id || apt.stylist;
        return id === stylistId;
      });
      setReviewableAppointmentId(match?._id || null);
    } catch (_) {
      // Non-critical — the write-review button will simply be hidden
    }
  };

  const handleAddReview = () => {
    setEditReview(null);
    setShowForm(true);
  };

  const handleEditReview = (review: any) => {
    setEditReview(review);
    setShowForm(true);
  };

  const handleReviewSuccess = () => {
    setShowForm(false);
    setEditReview(null);
    fetchReviews();
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditReview(null);
  };

  if (loading) {
    return (
      <div className="reviews__loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="message message--error">
        {error}
      </div>
    );
  }

  return (
    <div className="reviews">
      <div className="reviews__header">
        <h3 className="reviews__title">
          Client Reviews ({reviews.length})
        </h3>

        {isAuthenticated && !showForm && (
          userReview ? (
            <TeslaButton
              variant="secondary"
              size="sm"
              onClick={() => handleEditReview(userReview)}
            >
              <span className="reviews__button-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
              </span>
              Edit My Review
            </TeslaButton>
          ) : !userHasReviewed && reviewableAppointmentId ? (
            <TeslaButton
              variant="primary"
              size="sm"
              onClick={handleAddReview}
            >
              <span className="reviews__button-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </span>
              Write a Review
            </TeslaButton>
          ) : null
        )}
      </div>

      {showForm && (
        <div className="reviews__form-container">
          <ReviewForm
            stylistId={stylistId}
            stylistName={reviews[0]?.stylist?.name || ""}
            stylistPhoto={reviews[0]?.stylist?.profilePhoto?.url || ""}
            appointmentId={editReview ? undefined : (reviewableAppointmentId ?? undefined)}
            existingReview={editReview}
            onSuccess={handleReviewSuccess}
            onCancel={handleCancel}
          />
        </div>
      )}

      {reviews.length === 0 && !showForm ? (
        <div className="message message--info">
          No reviews available yet. Be the first to leave a review!
        </div>
      ) : (
        <div className="reviews__list">
          {reviews.map((review, index) => (
            <div key={review._id} className="review-item">
              {index > 0 && <div className="review-item__divider"></div>}

              <div className="review-item__container">
                <ProfileAvatar
                  className="review-item__avatar"
                  name={review.client?.name || 'Anonymous'}
                  photo={review.client?.profilePhoto}
                />

                <div className="review-item__content">
                  <div className="review-item__name">
                    {review.client?.name || 'Anonymous'}
                  </div>

                  <div className="review-item__meta">
                    <div className="review-item__rating">
                      {[1, 2, 3, 4, 5].map((value) => (
                        <span
                          key={value}
                          className={`review-item__star ${
                            value <= Math.round(
                            typeof review.rating === 'number'
                              ? review.rating
                              : review.rating?.overall ?? review.rating?.score ?? 0
                          ) ? 'active' : ''
                          }`}
                        >
                          ★
                        </span>
                      ))}
                    </div>
                    <div className="review-item__date">
                      {format(new Date(review.createdAt), 'MMM d, yyyy')}
                    </div>
                  </div>

                  {review.service && (
                    <div className="review-item__service">
                      {typeof review.service === 'string'
                        ? review.service
                        : review.service?.name || null}
                    </div>
                  )}

                  {review.review?.text && (
                    <p className="review-item__text">
                      {review.review.text}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StylistReviews;
